import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = newsList.find((n) => n.id === id);

  usePageMeta({
    title: article ? `${article.title} - 秋霞影院资讯` : "影视资讯详情 - 秋霞影院",
    description: article ? article.summary : "秋霞影院影视资讯详情，了解影视行业最新动态。",
    keywords: article ? `${article.title},影视资讯,秋霞影院` : "秋霞影院,影视资讯",
  });

  const related = useMemo(() => {
    if (!article) return [];
    const byTag = newsList.filter(
      (n) => n.id !== article.id && n.tags.some((t) => article.tags.includes(t))
    );
    const byCategory = newsList.filter(
      (n) => n.id !== article.id && n.category === article.category && !byTag.includes(n)
    );
    return [...byTag, ...byCategory].slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-file-unknow-line" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">资讯内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">该资讯可能已被删除或链接有误</p>
        <Link
          to="/news"
          className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
        >
          返回资讯列表
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="cursor-pointer transition-colors hover:text-accent-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to="/news" className="cursor-pointer transition-colors hover:text-accent-600">
          影视资讯
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="clamp-2 text-foreground-800">{article.title}</span>
      </nav>

      <article className="mt-5 rounded-xl border border-background-200 bg-background-50 p-5 md:p-8">
        <span className="inline-block rounded bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-700">
          {article.category}
        </span>
        <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
          {article.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-5 text-sm text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line" /> {article.date}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line" /> {article.author}
          </span>
        </div>

        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 秋霞影院资讯配图`}
          className="mt-6 h-56 w-full rounded-lg object-cover object-top md:h-80"
        />

        <div className="mt-6 space-y-4">
          {article.paragraphs.map((p, i) => (
            <p key={i} className="text-sm leading-8 text-foreground-700 md:text-base">
              {p}
            </p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2 border-t border-background-200 pt-6">
          {article.tags.map((tag) => (
            <Link
              key={tag}
              to={`/search?q=${encodeURIComponent(tag)}`}
              className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
            >
              #{tag}
            </Link>
          ))}
        </div>
      </article>

      <section className="mt-8">
        <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">相关文章</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {related.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              className="group flex cursor-pointer items-start gap-3 rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
            >
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 秋霞影院资讯`}
                className="h-16 w-20 shrink-0 rounded-md object-cover object-top"
              />
              <div>
                <h3 className="clamp-2 font-heading text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-xs text-foreground-400">{item.date}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}